import { IChoiceGroupOption } from '@fluentui/react';

// ── CSS Escalation ──

export type EscalationReason = 'dealBlocker' | 'technicalSupport' | 'nonTechnicalSupport';

export interface ICssEscalationData {
    cssCaseNumber: string;
    escalationReason: EscalationReason | '';
}

export const ESCALATION_REASON_OPTIONS: IChoiceGroupOption[] = [
    {
        key: 'dealBlocker',
        text: 'Deal-blocker',
        title: 'Immediate revenue loss, no workaround available',
    },
    {
        key: 'technicalSupport',
        text: 'Technical support',
        title: 'More technical depth needed',
    },
    {
        key: 'nonTechnicalSupport',
        text: 'Non-technical support',
        title: 'Device exchange issues, customer reassurance, etc',
    },
];

// ── Warranty Need ──

export type WarrantyHelpType =
    | 'warrantyStartDate'
    | 'warrantyTransfer'
    | 'outOfWarranty'
    | 'protectionPlanIssue'
    | 'otherWarranty';

export interface IWarrantyCustomerDetails {
    contactName: string;
    email: string;
    phone: string;
    shippingAddress: string;
}

export interface IWarrantyNeedData {
    helpType: WarrantyHelpType | '';
    additionalDetails: string;
    customerDetails: IWarrantyCustomerDetails;
}

export const WARRANTY_HELP_OPTIONS: IChoiceGroupOption[] = [
    { key: 'warrantyStartDate', text: 'Warranty start date' },
    { key: 'warrantyTransfer', text: 'Warranty transfer' },
    { key: 'outOfWarranty', text: 'Out-of-warranty request' },
    { key: 'protectionPlanIssue', text: 'Protection Plan issue' },
    { key: 'otherWarranty', text: 'Other warranty request' },
];

/** Preserves the seller-entered shipping address in the legacy combined DTO field. */
export function buildCompanyFullShippingAddress(address?: string | null): string {
    if (!address) return '';
    return address.trim();
}

/** Hydrates warranty contact details while retaining legacy structured draft addresses. */
export function normalizeWarrantyCustomerDetails(raw: any): IWarrantyCustomerDetails {
    const base = { contactName: raw?.contactName || '', email: raw?.email || '', phone: raw?.phone || '' };
    const address = raw?.shippingAddress;
    if (typeof address === 'string') return { ...base, shippingAddress: address };
    return {
        ...base,
        shippingAddress: [address?.street, address?.city, address?.state, address?.postalCode, address?.country]
            .filter(Boolean)
            .join(', '),
    };
}

// ── Feature Request ──

export type BusinessConsequence =
    | 'dealAtRisk'
    | 'smallerDeal'
    | 'competitiveGap'
    | 'extraEffort'
    | 'niceToHave';

export type PayMoreOption = 'yes' | 'noExpectStandard' | 'notDiscussed';

export interface IFeatureRequestData {
    businessConsequence: BusinessConsequence | '';
    payMore: PayMoreOption | '';
    priceUplift: string;
    keyCompetitors: string;
}

export const BUSINESS_CONSEQUENCE_OPTIONS: IChoiceGroupOption[] = [
    { key: 'dealAtRisk', text: 'Deal at risk' },
    { key: 'smallerDeal', text: 'Smaller deal' },
    { key: 'competitiveGap', text: 'Competitive gap' },
    { key: 'extraEffort', text: 'Extra effort' },
    { key: 'niceToHave', text: 'Nice to have' },
];

export const PAY_MORE_OPTIONS: IChoiceGroupOption[] = [
    { key: 'yes', text: 'Yes' },
    { key: 'noExpectStandard', text: 'No, expect as standard' },
    { key: 'notDiscussed', text: 'Not discussed / Unsure' },
];

export const PRICE_UPLIFT_SUGGESTIONS: string[] = [
    '<$10',
    '$10-25',
    '$25-50',
    '$50-100',
    '$100+',
];

export type SalesSubCategory = 'pricingLicensing' | 'dealConfiguration' | 'preSalesTechnical' | 'other';

export interface ISalesRequestData {
    subCategory: SalesSubCategory | '';
    otherText: string;
}

export const SALES_SUBCATEGORY_OPTIONS: IChoiceGroupOption[] = [
    { key: 'pricingLicensing', text: 'Pricing and licensing' },
    { key: 'dealConfiguration', text: 'Deal configuration' },
    { key: 'preSalesTechnical', text: 'Pre-sales technical guidance' },
    { key: 'other', text: 'Other' },
];

export const INITIAL_SALES_DATA: ISalesRequestData = {
    subCategory: '',
    otherText: '',
};

/** CPE sub-categories mapped to the CXP request type used at submission. */
export type CpeSubCategory = 'variant' | 'productFeature' | 'messaging' | 'technical' | 'program';

export interface ICpeSubCategoryOption {
    key: CpeSubCategory;
    text: string;
    requestType: number;
}

export const CPE_SUBCATEGORY_OPTIONS: ICpeSubCategoryOption[] = [
    { key: 'variant', text: 'Variant', requestType: 8 },
    { key: 'productFeature', text: 'Product feature', requestType: 9 },
    { key: 'messaging', text: 'Messaging', requestType: 10 },
    { key: 'technical', text: 'Technical', requestType: 11 },
    { key: 'program', text: 'Program', requestType: 12 },
];

export interface ICpeSupportData {
    subCategory: CpeSubCategory | '';
}

export const CPE_DOCUMENT_GUIDANCE =
    'Describe the customer or partner engagement and the outcome you need from the CPE team.';

export const INITIAL_CPE_DATA: ICpeSupportData = {
    subCategory: '',
};

// ── Contextual Details (combined) ──

export interface IContextualDetailsData {
    cssEscalation: ICssEscalationData;
    warrantyNeed: IWarrantyNeedData;
    featureRequest: IFeatureRequestData;
    salesRequest: ISalesRequestData;
    cpeSupport: ICpeSupportData;
}

export const INITIAL_CSS_ESCALATION_DATA: ICssEscalationData = {
    cssCaseNumber: '',
    escalationReason: '',
};

export const INITIAL_WARRANTY_DATA: IWarrantyNeedData = {
    helpType: '',
    additionalDetails: '',
    customerDetails: {
        contactName: '',
        email: '',
        phone: '',
        shippingAddress: '',
    },
};

export const INITIAL_FEATURE_REQUEST_DATA: IFeatureRequestData = {
    businessConsequence: '',
    payMore: '',
    priceUplift: '',
    keyCompetitors: '',
};

// ── Request type keys (variant of contextual details to render) ──

export const REQUEST_TYPE_KEYS = {
    ATTACHMENTS: 'attachments',
    CSS: 'css',
    WARRANTY: 'warranty',
    FEATURE: 'feature',
    MESSAGING: 'messaging',
    SALES: 'sales',
} as const;

export type RequestTypeKey = typeof REQUEST_TYPE_KEYS[keyof typeof REQUEST_TYPE_KEYS];
